// src/pages/healthcare/consultation/InPersonConsultation.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button, message, Typography, Modal, Tooltip } from "antd";
import { MobileOutlined } from "@ant-design/icons";

// API Imports
import { fetchMedicalHistories, updatePatient } from "../../../api/healthcare/PatientApi";
import { fetchSingleCompletedAppointmentById } from "../../../api/healthcare/Appointment/AppointmentApi";
import { fetchTests } from "../../../api/healthcare/Testing/TestApi";
import { fetchMedications } from "../../../api/healthcare/MedicationsApi";
import { fetchClinicCompany } from "../../../api/admin/companyApi";
import { requestMobileMicToken } from "../../../api/healthcare/Consultation/ConsultationReportApi";
import { Company } from "../../../types/admin/CompanyTypes";

// Types
import { Patient } from "../../../types/healthcare/PatientTypes";
import { Appointment, CompletedAppointment, defaultConsult } from "../../../types/appointments/AppointmentTypes";
import { PatientNote } from "../../../types/healthcare/PatientNotes";
import { consultParticipant, SpeakerMap, SpeakerParticipantInput } from "../../../types/appointments/ConsultTypes";
import { TeamMember } from "../../../types/admin/TeamTypes";

// Services
import SocketService from "../../../services/socketService";

// Hooks
import { useConsultTranscripts, useTranscriberStatus, safeParseTranscripts, transcriberStatusFromError, transcriberStatusFromEvent } from "../../../hooks/consult/useConsultTranscripts";
import { useTranscriberCapture } from "../../../hooks/consult/useTranscriberCapture";
import { useCaptureTelemetry } from "../../../hooks/consult/useCaptureTelemetry";
import { useFinalTranscript } from "../../../hooks/consult/useFinalTranscript";
import { useConsultSuggestions } from "../../../hooks/consult/useConsultSuggestions";
import { useDebounce } from "../../../hooks/consult/useDebounce";
import { useActiveTreatmentPlan } from "../../../hooks/useActiveTreatmentPlan";
import { useConsultSession } from "../../../hooks/consult/useConsultSession";
import { useXoeFocusPublisher } from "../../../context/XoeFocusContext";
import { useEndConsult } from "../../../hooks/consult/useEndConsult";
import { usePatientAutoSave } from "../../../hooks/consult/usePatientAutoSave";

// Utils
import { Suggestion } from "../../../utils/healthcare/SugestionUtils";
import { buildInPersonDisplayNames } from "../../../utils/consult/participantIdentity";
import { applySpeakerAssignment, emptySpeakerMap, parseSpeakerMap } from "../../../utils/consult/speakerNames";
import { buildMobileMicUrl } from "../../../utils/consult/mobileMicLink";
import { getPatientDataSnapshot } from "../../../utils/consult/patientSnapshot";
import { listInputDevices, seedDeviceSelection } from "../../../utils/consult/localMedia";

// Components
import EditPatientModal from "../patient/PatientModal";
import type { TranscriptListProps } from "./consultationComponents/Transcript";
import EndConsultModal from "./consultationComponents/EndConsultModal";
import AdvancedSettingsDialog from "./consultationComponents/AdvancedSettingsDialog";
import ConsultDrawerShell from "./consultationComponents/ConsultDrawerShell";
import ConsultDrawerTitle from "./consultationComponents/ConsultDrawerTitle";
import ConsultContentTabs from "./consultationComponents/ConsultContentTabs";
import ConsultRightPanel, { RightPanelMode } from "./consultationComponents/ConsultRightPanel";
import ConsultTranscriptDrawer from "./consultationComponents/ConsultTranscriptDrawer";

const { Text } = Typography;


interface InPersonConsultationProps {
	isModalOpen: boolean;
	handleCloseConsultationModal: () => void;
	appointment: Appointment;
	patient: Patient;
	setPatient: React.Dispatch<React.SetStateAction<Patient>>;
	practitioner: TeamMember;
}

const InPersonConsultation: React.FC<InPersonConsultationProps> = ({ isModalOpen, handleCloseConsultationModal, appointment, patient, setPatient, practitioner }) => {
	const [patientNotes, setPatientNotes] = useState<PatientNote[]>([]);
	const [company, setCompany] = useState<Company | null>(null);

	// The feed owns dedupe, ordering and every parse of a stored/broadcast value — see hooks/useConsultTranscripts.
	const { transcripts, upsert: upsertTranscript, upsertMany: upsertTranscripts, setAll: setAllTranscripts } = useConsultTranscripts();
	const { status: transcriberStatus, report: reportTranscriberStatus } = useTranscriberStatus();
	const [transcriptDrawerOpen, setTranscriptDrawerOpen] = useState<boolean>(false);

	const [consultation, setConsultation] = useState<CompletedAppointment>(defaultConsult);
	const [participants, setParticipants] = useState<consultParticipant[]>([]);

	// An in-person room has no roster: exactly two microphones, this device and an optionally paired phone.
	const displayNames = useMemo(
		() => buildInPersonDisplayNames(`${practitioner?.first_name || ""} ${practitioner?.last_name || ""}`),
		[practitioner?.first_name, practitioner?.last_name],
	);

	// talk owns the map (`assignSpeaker` → `speakerMapUpdated`) and keeps it on the room, so it survives a refresh through `roomInfo`.
	const [speakerMap, setSpeakerMap] = useState<SpeakerMap>(emptySpeakerMap);

	// The final pass replaces the segments outright but leaves the speaker map alone: transcribe re-uses the live labels and talk's final save posts no `speaker_map`.
	const applyFinalTranscript = useCallback(
		(upgraded: CompletedAppointment) => {
			setAllTranscripts(upgraded.transcript);
			setConsultation(upgraded);
		},
		[setAllTranscripts],
	);

	useFinalTranscript({
		appointmentId: appointment.id,
		status: consultation.transcript_status,
		enabled: appointment.status === "Completed",
		onFinal: applyFinalTranscript,
	});

	// Consult Guidance
	const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>("notes");
	const { consultSessionId } = useConsultSession({ appointmentId: appointment.id, enabled: appointment.status !== "Completed" });

	// S13 — a LIVE consult on the Xoe focus bus (presence `in_consult`); a completed one under review is not.
	const { setConsultFocus, clearConsultFocus } = useXoeFocusPublisher();
	useEffect(() => {
		if (appointment.status === "Completed") return;
		setConsultFocus(true);
		return () => clearConsultFocus();
	}, [appointment.status, setConsultFocus, clearConsultFocus]);

	// Active treatment plan
	const { activePlan, setActivePlan, reloadPlan, stageMatchesAppointment } = useActiveTreatmentPlan({ patientId: appointment.patient_id, appointmentTypeId: appointment.appointment_type_id, enabled: appointment.status !== "Completed" });
	// Tracks whether we auto-switched to plan_notes so session load doesn't override it
	const planNotesActiveRef = useRef(false);
	useEffect(() => {
		if (!stageMatchesAppointment) return;
		planNotesActiveRef.current = true;
		setRightPanelMode("plan_notes");
	}, [stageMatchesAppointment]);
	useEffect(() => {
		if (consultSessionId && !planNotesActiveRef.current) setRightPanelMode("notes");
	}, [consultSessionId]);

	// AI — off by default: an in-person consult opts in from the suggestions panel.
	const [aiActive, setAiActive] = useState<boolean>(false);
	const [aiEnabled, setAiEnabled] = useState<boolean>(false);
	const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

	// Modals
	const [isPatientModalOpen, setIsPatientModalOpen] = useState<boolean>(false);
	const [isEndModalOpen, setIsEndModalOpen] = useState<boolean>(false);
	const [advancedSettingsVisible, setAdvancedSettingsVisible] = useState<boolean>(false);

	// Device Settings
	const [microphones, setMicrophones] = useState<any[]>([]);
	const [mobileMicQRVisible, setMobileMicQRVisible] = useState(false);
	// The QR carries a 15-minute credential: minted when the modal opens, dropped when it closes, never cached across opens.
	const [mobileMicToken, setMobileMicToken] = useState<string | null>(null);
	const [mobileMicTokenError, setMobileMicTokenError] = useState<string>("");

	// Refs
	const socketServiceRef = useRef<SocketService | null>(null);
	const patientLoadedRef = useRef<boolean>(false);
	const patientDataSentRef = useRef<boolean>(false);
	const roomDataLoadedRef = useRef<boolean>(false);
	const roomDataRequestedRef = useRef<boolean>(false);
	const previousPatientDataRef = useRef<string>("");

	const debouncedPatient = useDebounce(patient, 1000);
	const { handleAutoSave } = usePatientAutoSave({ patient, roomId: appointment.room_number });

	// Declared above every consumer: a useCallback dep read before its declaration white-screens the drawer.
	// `participantId` stays the literal "practitioner" — buildInPersonDisplayNames and the mobile-mic naming key on it.
	const {
		consultationStarted,
		markConsultationStarted,
		startCapture,
		stopCapture,
		toggleTranscribeListener,
		transcriberEnabled,
		transcriberActive,
		captureState,
		audioStreamingServiceRef,
		setAudioStreamingService,
		selectedMicrophone,
		setSelectedMicrophone,
		switchMicrophone,
		getCaptureTrack,
		audioSettings,
		handleAudioSettingsChange,
		dispose: disposeCapture,
	} = useTranscriberCapture({ roomId: appointment.room_number, roomType: "inperson", participantId: "practitioner", enabled: appointment.status !== "Completed" });

	// The hook re-reads its options every render, so this closure always sees the current transcriberEnabled.
	useCaptureTelemetry({
		enabled: consultationStarted && appointment?.status !== "Completed",
		roomId: appointment.room_number,
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		getCapture: () => audioStreamingServiceRef.current,
		isDisabled: () => !transcriberEnabled,
	});

	// The capture's own track wins while one is live, so the picker opens on the microphone actually recording.
	const loadMicrophones = useCallback(async () => {
		const { microphones: audioDevices } = await listInputDevices();
		const inUse = getCaptureTrack();
		setMicrophones(audioDevices);
		if (audioDevices.length > 0) setSelectedMicrophone((prev) => seedDeviceSelection(audioDevices, inUse, inUse ? undefined : prev));
	}, [getCaptureTrack, setSelectedMicrophone]);

	const openSettings = useCallback(() => {
		loadMicrophones().catch(() => console.error("consult.devices.enumerate_failed", { roomId: appointment.room_number }));
		setAdvancedSettingsVisible(true);
	}, [loadMicrophones, appointment.room_number]);

	const applyMicrophone = useCallback(
		async (_cameraId: string, micId: string) => {
			if (await switchMicrophone(micId)) return true;
			message.error("Failed to access microphone");
			return false;
		},
		[switchMicrophone],
	);

	useEffect(() => {
		fetchClinicCompany().then(setCompany).catch(() => {});
	}, []);

	// Mint the mobile microphone's credential while the QR is on screen.
	useEffect(() => {
		if (!mobileMicQRVisible) {
			setMobileMicToken(null);
			setMobileMicTokenError("");
			return;
		}

		let active = true;
		setMobileMicToken(null);
		setMobileMicTokenError("");
		requestMobileMicToken(appointment.room_number)
			.then((res) => {
				if (active) setMobileMicToken(res.token);
			})
			.catch(() => {
				// The reason is talk's to log; the practitioner needs to know only that the QR can be retried by reopening it.
				if (active) setMobileMicTokenError("Could not prepare the mobile microphone. Close this window and try again.");
			});

		return () => {
			active = false;
		};
	}, [mobileMicQRVisible, appointment.room_number]);

	// Init
	useEffect(() => {
		const roomId = appointment.room_number;
		let isComponentMounted = true;
		let roomDataTimeout: ReturnType<typeof setTimeout>;

		// Registered below and removed again on unmount — the same list in both directions.
		// ⚠️ `connect` is DELIBERATELY NOT in this list: a bare `off("connect")` also strips SocketService's own handler, the only caller of `sendPendingAudioData()`.
		const consultSocketEvents = ["transcriptUpdate", "suggestionUpdate", "aiAnalysisUpdated", "roomInfo", "roomPatientData", "roomConsultationData", "transcriberStatus", "transcriptionError", "speakerMapUpdated"];

		// Assigned in `init()` below; removed by reference — never by a blanket `off("connect")`.
		let pageConnectHandler: (() => void) | null = null;

		async function loadPatientDataFromAPI() {
			if (patientLoadedRef.current) return;
			try {
				const medHist = await fetchMedicalHistories(appointment.patient_id);
				const tests = await fetchTests(appointment.patient_id);
				const { data: medications } = await fetchMedications(appointment.patient_id);
				if (!isComponentMounted) return;
				setPatient((prev) => ({ ...prev, medical_history: medHist, tests, medications }));
				setConsultation((prev) => (prev.app_id ? prev : { ...prev, app_id: appointment.id }));
				if (patient.patient_info?.notes && typeof patient.patient_info.notes === "string") {
					setPatientNotes(JSON.parse(patient.patient_info.notes));
				}
				patientLoadedRef.current = true;
			} catch {
				// Event name + room id ONLY: a SyntaxError from the notes parse quotes the clinical notes.
				console.error("consult.patient.load_failed", { roomId });
			}
		}

		async function init() {
			try {
				if (appointment.status !== "Completed") {
					if (socketServiceRef.current) socketServiceRef.current.disconnect();

					socketServiceRef.current = SocketService.getInstance();
					const socket: any = socketServiceRef.current.initialize(roomId, "inperson");

					// No `socket.onAny` logger here, ever: it printed every payload the room emitted into the browser console.
					consultSocketEvents.forEach((ev) => socket.off(ev));

					socket.on("roomPatientData", (data: any) => {
						if (!isComponentMounted) return;
						try {
							if (data.patient) {
								const roomPatient = typeof data.patient === "string" ? JSON.parse(data.patient) : data.patient;
								setPatient((prev) => ({ ...prev, ...roomPatient }));
								if (roomPatient.patient_info?.notes && typeof roomPatient.patient_info.notes === "string") {
									setPatientNotes(JSON.parse(roomPatient.patient_info.notes));
								}
								patientLoadedRef.current = true;
								roomDataLoadedRef.current = true;
								previousPatientDataRef.current = getPatientDataSnapshot(roomPatient);
							}
						} catch {
							// Event name + room id ONLY, never the exception: every payload parsed on this socket carries the patient record or the notes. Same rule for the catches below.
							console.error("consult.room_patient_data.parse_failed", { roomId });
							loadPatientDataFromAPI();
						}
					});

					socket.on("roomConsultationData", (data: any) => {
						if (!isComponentMounted) return;
						try {
							if (data.consultationData) {
								const rc = typeof data.consultationData === "string" ? JSON.parse(data.consultationData) : data.consultationData;
								setConsultation((prev) => ({ ...prev, ...rc }));
							}
						} catch {
							console.error("consult.room_consultation_data.parse_failed", { roomId });
						}
					});

					socket.on("transcriptUpdate", (data: any) => {
						if (!isComponentMounted) return;
						upsertTranscript(data);
					});

					socket.on("suggestionUpdate", (data: any) => {
						if (!isComponentMounted) return;
						if (data.suggestions && Array.isArray(data.suggestions)) setSuggestions(data.suggestions);
					});

					// talk owns the room's AI flag and broadcasts it back to the emitter too, so a local toggle round-trips and lands here idempotently.
					socket.on("aiAnalysisUpdated", (data: any) => {
						if (!isComponentMounted) return;
						// talk emits `{ roomId, isActive: 'active'|'inactive', timestamp }` — read the one field it sends.
						const active = data?.isActive === "active";
						setAiEnabled(active);
						setAiActive(active);
					});

					socket.on("transcriberStatus", (data: any) => {
						if (!isComponentMounted) return;
						reportTranscriberStatus(transcriberStatusFromEvent(data));
					});

					socket.on("transcriptionError", (data: any) => {
						if (!isComponentMounted) return;
						reportTranscriberStatus(transcriberStatusFromError(data));
					});

					// The whole map, every time — talk broadcasts it after every assignment so two open tabs converge.
					socket.on("speakerMapUpdated", (data: any) => {
						if (!isComponentMounted) return;
						setSpeakerMap(parseSpeakerMap(data?.speakerMap));
					});

					socket.on("roomInfo", (data: any) => {
						if (!isComponentMounted) return;
						if (roomDataTimeout) clearTimeout(roomDataTimeout);
						let hasData = false;

						if (data.participants && typeof data.participants === "string") {
							try {
								const p = JSON.parse(data.participants);
								if (Array.isArray(p) && p.length > 0) {
									markConsultationStarted();
									setParticipants(p);
									hasData = true;
								}
							} catch {
								console.error("consult.room_info.participants.parse_failed", { roomId });
							}
						}
						// UNIONED, never assigned: `roomInfo` answers every `joinRoom`, and talk emits `transcriptUpdate` before it appends to Redis — replacing the feed would erase a segment caught in that window.
						if (data.transcripts) {
							const t = safeParseTranscripts(data.transcripts);
							if (t.length > 0) {
								markConsultationStarted();
								upsertTranscripts(t);
								hasData = true;
							}
						}
						// Only a snapshot that HAS names replaces the map: talk's Redis-down path sends `speakerMap: '{}'`, which is truthy. Clearing a name is `speakerMapUpdated`'s job.
						const roomSpeakerMap = parseSpeakerMap(data.speakerMap);
						if (Object.keys(roomSpeakerMap).length > 0) {
							setSpeakerMap(roomSpeakerMap);
						}
						if (data.suggestions && typeof data.suggestions === "string") {
							try {
								const s = JSON.parse(data.suggestions);
								if (Array.isArray(s) && s.length > 0) {
									markConsultationStarted();
									setSuggestions(s);
									hasData = true;
								}
							} catch {
								console.error("consult.room_info.suggestions.parse_failed", { roomId });
							}
						}
						if (data.patientData && typeof data.patientData === "string") {
							try {
								const rp = JSON.parse(data.patientData);
								setPatient((prev) => ({ ...prev, ...rp }));
								if (rp.patient_info?.notes && typeof rp.patient_info.notes === "string") setPatientNotes(JSON.parse(rp.patient_info.notes));
								patientLoadedRef.current = true;
								roomDataLoadedRef.current = true;
								previousPatientDataRef.current = getPatientDataSnapshot(rp);
								hasData = true;
							} catch {
								console.error("consult.room_info.patient_data.parse_failed", { roomId });
							}
						}
						if (data.consultationData && typeof data.consultationData === "string") {
							try {
								const rc = JSON.parse(data.consultationData);
								setConsultation((prev) => ({ ...prev, ...rc }));
								hasData = true;
							} catch {
								console.error("consult.room_info.consultation_data.parse_failed", { roomId });
							}
						}

						if (!hasData || (!data.patientData && !patientLoadedRef.current)) loadPatientDataFromAPI();
						else roomDataLoadedRef.current = true;
					});

					pageConnectHandler = () => {
						socket.emit("joinRoom", roomId);

						if (!roomDataRequestedRef.current) {
							socket.emit("requestRoomData", roomId);
							roomDataRequestedRef.current = true;
							roomDataTimeout = setTimeout(() => {
								if (!roomDataLoadedRef.current && !patientLoadedRef.current) loadPatientDataFromAPI();
							}, 3000);
						}

						if (patientLoadedRef.current && roomDataLoadedRef.current) {
							socket.emit("setPatientData", { roomId, patient, appId: appointment.id });
							previousPatientDataRef.current = getPatientDataSnapshot(patient);
							patientDataSentRef.current = true;
						}
					};
					socket.on("connect", pageConnectHandler);

					try {
						await loadMicrophones();
					} catch {
						console.error("consult.devices.enumerate_failed", { roomId });
					}
				} else {
					const { data } = await fetchSingleCompletedAppointmentById(appointment.id);
					if (!isComponentMounted) return;
					if (data) {
						// A malformed stored column yields an empty transcript panel, not a thrown init that also loses the record.
						setAllTranscripts(data.transcript);
						// This branch opens no socket, so the stored column is the ONLY source of the names the practitioner typed.
						setSpeakerMap(parseSpeakerMap(data.speaker_map));
						setSuggestions(data.suggestions !== "" ? JSON.parse(data.suggestions as string) : []);
						setConsultation(data);
					}
				}
			} catch {
				// Event name + room id ONLY: this block JSON.parses the stored suggestions.
				console.error("consult.init_failed", { roomId });
				message.error("Failed to initialize consultation");
			}
		}

		init();

		return () => {
			isComponentMounted = false;
			if (roomDataTimeout) clearTimeout(roomDataTimeout);
			if (socketServiceRef.current) {
				// Symmetric with the registration above. `connect` goes by named reference so the service's own handler — the one that flushes queued audio — survives.
				const socket: any = socketServiceRef.current.getSocket();
				if (socket) {
					consultSocketEvents.forEach((ev) => socket.off(ev));
					if (pageConnectHandler) socket.off("connect", pageConnectHandler);
				}
				socketServiceRef.current.disconnect();
				socketServiceRef.current = null;
			}
			disposeCapture();
		};
		// Every callback here is identity-stable per room, so this effect re-runs only when the consult itself changes.
	}, [appointment.room_number, appointment.id, appointment.status, appointment.patient_id, markConsultationStarted, disposeCapture, loadMicrophones]);

	// Sync: initial patient data send (only if loaded from API, not from room)
	useEffect(() => {
		if (patientLoadedRef.current && !patientDataSentRef.current && !roomDataLoadedRef.current && socketServiceRef.current) {
			const socket: any = socketServiceRef.current.getSocket();
			if (socket?.connected) {
				// appId stamps the room with the appointment it holds; talk refuses to hand an unowned room to the final pass.
				socket.emit("setPatientData", { roomId: appointment.room_number, patient, appId: appointment.id });
				previousPatientDataRef.current = getPatientDataSnapshot(patient);
				patientDataSentRef.current = true;
			}
		}
	}, [patient, appointment.room_number, appointment.id]);

	// Mark patient data as sent when loaded from room
	useEffect(() => {
		if (roomDataLoadedRef.current && patientLoadedRef.current && !patientDataSentRef.current) {
			previousPatientDataRef.current = getPatientDataSnapshot(patient);
			patientDataSentRef.current = true;
		}
	}, [patient]);

	// Debounced patient data sync
	useEffect(() => {
		if (!patientLoadedRef.current || !patientDataSentRef.current || appointment.status === "Completed") return;
		const snap = getPatientDataSnapshot(debouncedPatient);
		if (snap !== previousPatientDataRef.current) {
			const si: any = socketServiceRef.current?.getSocket();
			if (si?.connected) {
				si.emit("updatePatientData", { roomId: appointment.room_number, patient: debouncedPatient, changeType: "incremental_update", timestamp: Date.now() });
				previousPatientDataRef.current = snap;
				message.success("Patient data updated for AI suggestions", 2);
			}
		}
	}, [debouncedPatient, appointment.room_number, appointment.status]);

	// Consultation notes/vitals sync
	useEffect(() => {
		const si: any = socketServiceRef.current?.getSocket();
		if (si && consultation) {
			si.emit("setConsultationData", {
				roomId: appointment.room_number,
				consultationNotes: consultation.notes || "",
				consultParticulars: consultation.vitals || "",
				// Second writer of the room's app_id: whichever emit lands first stamps the room.
				appId: appointment.id,
			});
		}
	}, [consultation.notes, consultation.vitals, appointment.room_number, appointment.id]);

	// Participant sync
	useEffect(() => {
		const si: any = socketServiceRef.current?.getSocket();
		if (si) {
			si.emit("setRoomParticipants", {
				roomId: appointment.room_number,
				participantCount: participants.length,
				participants: JSON.stringify(participants),
				aiActive: aiEnabled ? "active" : "inactive",
				transcriberActive: transcriberEnabled,
			});
		}
	}, [participants, appointment.room_number, transcriberEnabled, aiEnabled]);

	// Accept/dismiss is still talk's write; only the generation moved.
	const updateSuggestionStatus = useCallback(
		(suggestionId: string, updates: Partial<Suggestion>) => {
			const si: any = socketServiceRef.current?.getSocket();
			if (si) si.emit("updateSuggestion", { roomId: appointment.room_number, suggestionId, updates });
		},
		[appointment.room_number],
	);

	// This tab runs the pass itself and pushes the merged set BACK to talk, which stays the room's store and router.
	const { runRound: runSuggestionRound } = useConsultSuggestions({
		roomId: appointment.room_number,
		patient,
		transcripts,
		suggestions,
		consultationNotes: consultation.notes || "",
		consultParticulars: consultation.vitals || "",
		// A completed record loads its whole transcript on open and must never generate.
		enabled: aiEnabled && appointment.status !== "Completed",
	});

	// Read from an effect rather than the socket handler so the pass sees the segment that triggered it; the cooldown and minimums live in the hook.
	useEffect(() => {
		runSuggestionRound().then((round) => {
			// No cancel-on-cleanup: the emit's target is the ROOM, and an unmounted page has no socket.
			if (!round?.hasChanges) return;
			const si: any = socketServiceRef.current?.getSocket();
			si?.emit("setSuggestions", { roomId: appointment.room_number, suggestions: round.suggestions });
		});
	}, [transcripts, runSuggestionRound, appointment.room_number]);

	// Optimistic, then reconciled: talk's `speakerMapUpdated` replaces the map wholesale a moment later.
	const handleAssignSpeaker = useCallback(
		(speakerLabel: string, participant: SpeakerParticipantInput | null) => {
			setSpeakerMap((prev) => applySpeakerAssignment(prev, speakerLabel, participant));
			const si: any = socketServiceRef.current?.getSocket();
			si?.emit("assignSpeaker", { roomId: appointment.room_number, speakerLabel, participant });
		},
		[appointment.room_number],
	);

	const startConsultation = useCallback(async () => {
		// Before the emit: a refused microphone aborts the start; a transcriber the practitioner turned off does not.
		const ok = await startCapture();
		if (!ok && transcriberEnabled) {
			message.error("Failed to access microphone");
			return;
		}

		const socket: any = socketServiceRef.current?.getSocket();
		socket?.emit("startConsultation", appointment.room_number);

		// What makes a refresh restore "started": talk echoes these back as `roomInfo.participants`.
		const sanitizedPractitioner = {
			id: practitioner.id,
			uuid: practitioner.uuid,
			username: practitioner.username,
			email: practitioner.email,
			first_name: practitioner.first_name,
			last_name: practitioner.last_name,
			preferences: "",
			roles: [],
			team_member_info: { id: practitioner.team_member_info?.id },
		};
		const sanitizedPatient = {
			id: patient.id,
			uuid: patient.uuid,
			username: patient.username,
			email: patient.email,
			first_name: patient.first_name,
			last_name: patient.last_name,
			preferences: "",
			roles: [],
			medical_history: [],
			tests: [],
			medications: [],
			patient_info: {},
		};
		setParticipants((prev) => [
			...prev.filter((p) => p.role !== "Lead Practitioner" && p.role !== "Patient"),
			{ speakerTag: "", participant: sanitizedPractitioner as unknown as TeamMember, role: "Lead Practitioner" },
			{ speakerTag: "", participant: sanitizedPatient as unknown as Patient, role: "Patient" },
		]);

		setAiActive(aiEnabled);
		message.success("Consultation started");
	}, [aiEnabled, appointment.room_number, startCapture, transcriberEnabled, practitioner, patient]);

	const stopConsultation = useCallback(() => {
		stopCapture();

		// Tells transcribe to drop the room's speaker/VAD state.
		const socket: any = socketServiceRef.current?.getSocket();
		socket?.emit("endConsultation", appointment.room_number);

		setAiActive(false);
		setIsEndModalOpen(true);
		message.success("Consultation ended");
	}, [appointment.room_number, stopCapture]);

	// The flag is ROOM state, so it goes over the socket: talk writes it and broadcasts `aiAnalysisUpdated` back.
	const toggleAI = useCallback(() => {
		const newAiEnabled = !aiEnabled;
		setAiEnabled(newAiEnabled);
		setAiActive(newAiEnabled);

		const si: any = socketServiceRef.current?.getSocket();
		si?.emit("setAiAnalysis", {
			roomId: appointment.room_number,
			isActive: newAiEnabled ? "active" : "inactive",
		});
	}, [aiEnabled, appointment.room_number]);

	const updateConsultVitals = useCallback((data: string) => {
		setConsultation((prev) => ({ ...prev, vitals: data }));
	}, []);

	useEffect(() => {
		setPatient((prev) => ({ ...prev, patient_info: { ...prev.patient_info, notes: JSON.stringify(patientNotes) } }));
	}, [patientNotes]);

	const handleSavePatientNotes = async () => {
		await updatePatient(patient.id, patient);
		message.success("Notes saved successfully");
	};

	const { endConsult } = useEndConsult({ appointment, patient, roomId: appointment.room_number });

	const readOnly = appointment.status === "Completed";

	// One object feeds both TranscriptList render sites (right panel and side drawer), so the speaker chips stay live in both.
	const transcriptProps: TranscriptListProps = {
		patient,
		practitioner,
		transcripts,
		toggleTranscriber: toggleTranscribeListener,
		transcriberActive,
		isDisabled: readOnly,
		audioStreamingServiceRef,
		displayNames,
		speakerMap,
		onAssignSpeaker: handleAssignSpeaker,
		transcriberStatus,
		transcriptStatus: consultation.transcript_status,
	};

	// In-person-only toolbar button; the shared title slots it between Profile and Settings.
	const titleActions = (
		<Tooltip title="Mobile Microphone">
			<Button
				type="text"
				size="small"
				icon={<MobileOutlined />}
				data-testid="consultation-mobile-mic-btn"
				onClick={() => {
					// The phone takes over as the room's microphone, so this device's capture goes first.
					audioStreamingServiceRef.current?.stop();
					setAudioStreamingService(null);
					setMobileMicQRVisible(true);
				}}
			/>
		</Tooltip>
	);

	const drawerTitle = (
		<ConsultDrawerTitle
			patient={patient}
			appointment={appointment}
			captureState={captureState}
			consultationStarted={consultationStarted}
			onStart={startConsultation}
			onStop={stopConsultation}
			transcriptCount={transcripts.length}
			onOpenTranscript={() => setTranscriptDrawerOpen(true)}
			onOpenPatientProfile={() => setIsPatientModalOpen(true)}
			onOpenSettings={openSettings}
			readOnly={readOnly}
			actions={titleActions}
		/>
	);

	return (
		<>
			<ConsultDrawerShell
				title={drawerTitle}
				open={isModalOpen}
				onClose={handleCloseConsultationModal}
				captureState={captureState}
				onResumeCapture={toggleTranscribeListener}
				left={
					<ConsultContentTabs
						appointment={appointment}
						patient={patient}
						setPatient={setPatient}
						consultation={consultation}
						onVitalsChange={updateConsultVitals}
						readOnly={readOnly}
						consultSessionId={consultSessionId}
						activePlan={activePlan}
						onActivePlanClose={() => setActivePlan(null)}
						onPlanChange={reloadPlan}
						company={company}
						handleAutoSave={handleAutoSave}
					/>
				}
				right={
					<ConsultRightPanel
						mode={rightPanelMode}
						onModeChange={setRightPanelMode}
						activePlan={activePlan}
						consultSessionId={consultSessionId}
						patient={patient}
						setPatient={setPatient}
						practitioner={practitioner}
						appointment={appointment}
						isDisabled={readOnly}
						suggestions={{ items: suggestions, aiActive, toggleAI, updateSuggestion: updateSuggestionStatus }}
						notes={{ items: patientNotes, setItems: setPatientNotes, onSave: handleSavePatientNotes }}
						transcript={transcriptProps}
					/>
				}
			/>

			<ConsultTranscriptDrawer open={transcriptDrawerOpen} onClose={() => setTranscriptDrawerOpen(false)} count={transcripts.length} transcript={transcriptProps} />

			{advancedSettingsVisible && (
				<AdvancedSettingsDialog
					visible={advancedSettingsVisible}
					onHide={() => setAdvancedSettingsVisible(false)}
					showCamera={false}
					cameras={[]}
					selectedCamera=""
					microphones={microphones}
					selectedMicrophone={selectedMicrophone}
					onDevicesApply={applyMicrophone}
					voiceThreshold={audioSettings.voiceThreshold}
					silenceTimeout={audioSettings.silenceTimeout}
					noiseSuppression={audioSettings.noiseSuppression}
					echoCancellation={audioSettings.echoCancellation}
					autoGainControl={audioSettings.autoGainControl}
					onSettingsChange={handleAudioSettingsChange}
				/>
			)}

			{isPatientModalOpen && <EditPatientModal patient={patient} isOpen={isPatientModalOpen} onClose={() => setIsPatientModalOpen(false)} />}

			{/* `transcript={transcripts}`, never `[]` — every in-person consult once saved an empty transcript. */}
			{isEndModalOpen && <EndConsultModal isOpen={isEndModalOpen} closeModal={() => {setIsEndModalOpen(false); handleCloseConsultationModal();}} onFinalise={endConsult} patient={patient} setPatient={setPatient} suggestions={suggestions} transcript={transcripts} appointment={appointment} consult={consultation} speakerMap={speakerMap} roomId={appointment.room_number} />}

			<Modal title="Connect Mobile Microphone" open={mobileMicQRVisible} onCancel={() => setMobileMicQRVisible(false)} footer={null} width={400} zIndex={2001}>
				<div style={{ textAlign: "center" }}>
					<Text>Scan this QR code with your mobile phone to use it as the microphone for this consult.</Text>
					<div style={{ marginTop: 16, display: "flex", justifyContent: "center", minHeight: 256, alignItems: "center" }}>
						{/* Never rendered before the token arrives: a QR without one is a link talk refuses. */}
						{mobileMicToken ? (
							<QRCodeCanvas data-testid="consultation-mobile-mic-qr" value={buildMobileMicUrl(window.location.origin, appointment.room_number, mobileMicToken)} size={256} />
						) : (
							<Text type={mobileMicTokenError ? "danger" : "secondary"} data-testid="consultation-mobile-mic-status">
								{mobileMicTokenError || "Preparing a secure link…"}
							</Text>
						)}
					</div>
					{mobileMicToken && (
						<Text type="secondary" style={{ fontSize: 11 }}>
							This code is valid for 15 minutes. Reopen this window for a new one.
						</Text>
					)}
				</div>
			</Modal>
		</>
	);
};

export default InPersonConsultation;
